'use client'

import { MapPin, Edit3, Check, X, GripVertical } from "lucide-react"
import { Link } from "@tanstack/react-router"
import { Stop } from "@city-sage/core-client"
import { Button, Image, useRepository } from "@city-sage/aurum-avis-labs-components"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { QueryKeys } from "../lib/QueryKeys"
import { MarkdownRenderer } from "./MarkdownRenderer"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

interface StopPreviewCardProps {
  stop: Stop
  stopNumber: number
  itineraryId: string
  dayId: string
  isSelectable?: boolean
  isSelected?: boolean
  editable?: boolean
  isDraggable?: boolean
}

export function StopPreviewCard({ stop, stopNumber, itineraryId, dayId, isSelectable = false, isSelected = false, editable = false, isDraggable = false }: StopPreviewCardProps) {
  const { api } = useRepository()
  const queryClient = useQueryClient()

  // Drag and drop setup
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: stop.id,
    disabled: !isDraggable || isSelectable
  })

  // Mutation to accept AI suggestion
  const acceptSuggestionMutation = useMutation({
    mutationFn: () => api.updateStop({
      itineraryId,
      id: stop.id,
      updateStopRequest: { state: 'ACCEPTED' }
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey.includes(itineraryId) 
      })
    },
    onError: () => {
      toast.error("Failed to accept suggestion. Please try again.")
    }
  })

  // Mutation to reject AI suggestion
  const rejectSuggestionMutation = useMutation({
    mutationFn: () => api.updateStop({
      itineraryId,
      id: stop.id,
      updateStopRequest: { state: 'REJECTED' }
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        predicate: (query) => query.queryKey.includes(itineraryId) 
      })
    },
    onError: () => {
      toast.error("Failed to reject suggestion. Please try again.")
    }
  })

  const isSuggested = stop.state === 'SUGGESTED'
  const isUpdating = acceptSuggestionMutation.isPending || rejectSuggestionMutation.isPending

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    scale: isDragging ? 1.02 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  }

  const cardContent = (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-3 py-3 px-3 rounded-lg border ${
        isDragging ? 'shadow-lg z-50' : 'shadow-sm'
      } ${
        isSuggested 
          ? 'bg-blue-50 border-blue-200' 
          : 'bg-white border-gray-200'
      }`}
    >
      {/* Drag Handle - only show when draggable and not in suggested state */}
      {isDraggable && !isSuggested && !isSelectable && (
        <div 
          className="flex items-start pt-2 cursor-grab active:cursor-grabbing touch-none"
          style={{ touchAction: 'none', minHeight: '32px', minWidth: '20px' }}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600" />
        </div>
      )}

      {/* Image Section */}
      <div className="w-20 h-20 flex-shrink-0 relative">
        <Image
          src={stop.imageUrl}
          alt={stop.title}
          className="w-full h-full rounded-lg overflow-hidden"
          imageClassName="object-cover w-full h-full"
        />
        {/* Stop Number Badge */}
        <div className="absolute -top-1 -left-1 w-5 h-5 bg-white rounded-full flex items-center justify-center text-xs font-medium shadow-sm">
          {stopNumber}
        </div>
      </div>

      {/* Content Section */}
      <div className="flex-1 min-w-0">
        {/* Header with Title and Action Buttons */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 line-clamp-1 text-sm">{stop.title}</h4>
            <div className="text-xs text-gray-500 line-clamp-1">
              <MarkdownRenderer content={stop.highlight ?? stop.description ?? ''} className="text-xs text-gray-500" />
            </div>
          </div>
          
          <div className="flex items-center gap-1 flex-shrink-0">
            {!isSelectable && (
              <>
                {/* Accept/Reject buttons for suggested stops */}
                {isSuggested && (
                  <>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 bg-green-100 text-green-700 hover:bg-green-200 hover:text-green-800 border border-green-200 shadow-sm"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        acceptSuggestionMutation.mutate()
                      }}
                      disabled={isUpdating}
                      title="Accept suggestion"
                    >
                      <Check className="h-4 w-4" />
                      <span className="sr-only">Accept suggestion</span>
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 bg-red-100 text-red-700 hover:bg-red-200 hover:text-red-800 border border-red-200 shadow-sm"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        rejectSuggestionMutation.mutate()
                      }}
                      disabled={isUpdating}
                      title="Reject suggestion"
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">Reject suggestion</span>
                    </Button>
                  </>
                )}
                
                {/* Edit button */}
                {!isSuggested && editable && <Link
                  to="/itineraries/$itineraryId/days/$dayId/stops/$stopId/form"
                  params={{ itineraryId, dayId, stopId: stop.id }}
                >
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0 flex-shrink-0 text-gray-400 hover:text-gray-600">
                    <Edit3 className="h-3 w-3" />
                    <span className="sr-only">Edit stop</span>
                  </Button>
                </Link>}
              </>
            )}
            
            {/* Selection indicator */}
            {isSelectable && (
              <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center ${
                isSelected 
                  ? 'bg-primary border-primary' 
                  : 'border-gray-300'
              }`}>
                {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
              </div>
            )}
          </div>
        </div>

        {/* Address */}
        {stop.place.address && (
          <div className="flex items-center gap-1 mb-1">
            <MapPin className="h-3 w-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-500 line-clamp-1">
              {[stop.place.address.city, stop.place.address.countryName].filter(Boolean).join(", ")}
            </span>
          </div>
        )}

        {/* Timing Information */}
        {(stop.startTime || stop.durationMinutes) && (
          <div className="flex items-center gap-1.5 mb-1">
            {stop.startTime && (
              <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-xs font-medium">
                {stop.startTime}
              </span>
            )}
            {stop.durationMinutes && (
              <span className="bg-green-50 text-green-700 px-1.5 py-0.5 rounded text-xs font-medium">
                {stop.durationMinutes}min
              </span>
            )}
          </div>
        )}

        {/* Description Preview */}
        {stop.description && (
          <div className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
            <MarkdownRenderer content={stop.description} className="text-xs text-gray-600" />
          </div>
        )}
      </div>
    </div>
  )

  // If draggable or selectable, just return the card content
  if (isDraggable || isSelectable) {
    return cardContent
  }

  // Otherwise, wrap with Link for navigation
  return (
    <Link
      to="/itineraries/$itineraryId/days/$dayId/stops/$stopId/form"
      params={{ itineraryId, dayId, stopId: stop.id }}
      className="cursor-pointer w-full"
    >
      {cardContent}
    </Link>
  )
} 